import { ReactNode } from 'react';
import {
  Box,
  Image,
  Button,
  VStack,
  Text,
  IconButton,
  Skeleton
} from '@chakra-ui/react';
import { Upload, X } from 'lucide-react';
import {
  useCloudinaryUpload,
  type UseCloudinaryUploadOptions,
  type CloudinaryUploadResult
} from '@/hooks/useCloudinaryUpload';

type UploadMode = 'widget' | 'direct';

interface CloudinaryUploadButtonProps {
  // Display
  currentUrl?: string;
  placeholder?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  shape?: 'square' | 'rounded' | 'circle';

  // Upload config
  mode?: UploadMode;
  cropping?: boolean;
  aspectRatio?: number;
  maxFileSize?: number;
  allowedFormats?: string[];

  // Callbacks
  onUpload: (result: CloudinaryUploadResult) => void;
  onRemove?: () => void;

  /**
   * Opt-in "No photo selected" toast when a pick is abandoned without a
   * successful upload — forwarded to `useCloudinaryUpload`'s option of the
   * same name. Defaults to `false`/undefined so existing callers see zero
   * behavior change; set explicitly per call site, not globally.
   */
  notifyOnEmptyClose?: boolean;

  // UI options
  showRemoveButton?: boolean;
  uploadLabel?: string;
  changeLabel?: string;
  helpText?: string;
}

const SIZE_MAP = {
  sm: { box: '80px', icon: 24, button: 'xs' },
  md: { box: '120px', icon: 40, button: 'sm' },
  lg: { box: '160px', icon: 48, button: 'md' }
} as const;

const SHAPE_MAP = {
  square: 'md',
  rounded: 'xl',
  circle: 'full'
} as const;

export function CloudinaryUploadButton({
  currentUrl,
  placeholder,
  size = 'md',
  shape = 'rounded',
  mode = 'widget',
  cropping = false,
  aspectRatio = 1,
  maxFileSize,
  allowedFormats,
  onUpload,
  onRemove,
  notifyOnEmptyClose,
  showRemoveButton = true,
  uploadLabel = 'Upload Picture',
  changeLabel = 'Change Picture',
  helpText = 'JPG, PNG, or WEBP • Max 30MB • Large images are auto-optimized'
}: CloudinaryUploadButtonProps) {
  const sizeConfig = SIZE_MAP[size];
  const borderRadius = SHAPE_MAP[shape];

  const handleSuccess = (result: CloudinaryUploadResult) => {
    onUpload(result);
  };

  const uploadOptions: UseCloudinaryUploadOptions = {
    mode,
    cropping,
    aspectRatio,
    onSuccess: handleSuccess
  };

  if (notifyOnEmptyClose !== undefined) {
    uploadOptions.notifyOnEmptyClose = notifyOnEmptyClose;
  }

  if (maxFileSize !== undefined) {
    uploadOptions.maxFileSize = maxFileSize;
  }

  if (allowedFormats !== undefined) {
    uploadOptions.allowedFormats = allowedFormats;
  }

  const { openUploadWidget, openFilePicker, isUploading, pendingFile, confirmUpload, clearPendingFile } =
    useCloudinaryUpload(uploadOptions);

  const handleUploadClick = () => {
    if (pendingFile) {
      // Two-step flow: member has already selected a file — this click confirms and begins upload
      confirmUpload();
    } else if (mode === 'widget') {
      openUploadWidget();
    } else {
      openFilePicker();
    }
  };

  const handleRemove = () => {
    onRemove?.();
  };

  return (
    <VStack spacing={4}>
      <Box position="relative">
        <Box
          width={sizeConfig.box}
          height={sizeConfig.box}
          borderRadius={borderRadius}
          border="2px solid"
          borderColor="purple.500"
          overflow="hidden"
          bg="gray.700"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {isUploading ? (
            <Skeleton
              height="100%"
              width="100%"
              borderRadius={borderRadius}
              startColor="purple.900"
              endColor="purple.800"
            />
          ) : currentUrl ? (
            <Image
              src={currentUrl}
              alt="Upload preview"
              width="100%"
              height="100%"
              objectFit="cover"
            />
          ) : placeholder ? (
            placeholder
          ) : (
            <Upload size={sizeConfig.icon} color="var(--chakra-colors-gray-400)" />
          )}
        </Box>

        {currentUrl && !isUploading && showRemoveButton && onRemove && (
          <IconButton
            aria-label="Remove image"
            size="sm"
            bg="red.500"
            color="white"
            _hover={{ bg: 'red.600' }}
            position="absolute"
            top="-8px"
            right="-8px"
            borderRadius="full"
            onClick={handleRemove}
          >
            <X size={16} />
          </IconButton>
        )}
      </Box>

      <VStack spacing={2}>
        <Button
          leftIcon={<Upload size={20} />}
          variant="outline"
          color="purple.500"
          borderColor="purple.500"
          _hover={{ bg: 'purple.50' }}
          size={sizeConfig.button}
          onClick={handleUploadClick}
          isLoading={isUploading}
          loadingText="Uploading..."
        >
          {isUploading
            ? null // handled by isLoading / loadingText
            : pendingFile
              ? 'Use This Photo'
              : currentUrl
                ? changeLabel
                : uploadLabel}
        </Button>

        {pendingFile && !isUploading && (
          <Text
            as="button"
            type="button"
            fontSize="xs"
            color="gray.400"
            textDecoration="underline"
            cursor="pointer"
            onClick={clearPendingFile}
            _hover={{ color: 'gray.200' }}
          >
            Cancel
          </Text>
        )}

        {helpText && (
          <Text fontSize="xs" color="gray.400" textAlign="center">
            {helpText}
          </Text>
        )}
      </VStack>
    </VStack>
  );
}
